/**
 * Recap notes — the project's closeout capture log.
 *
 * The crew jot a line + photos as they work; the supervisor reads them on the
 * closeout screen as source material for the single recap blurb + curated photo
 * gallery. Purely INTERNAL capture — the crew's note text never ships to the
 * requester (the supervisor's narrative does), and there are NO @mentions or
 * notifications (unlike crew chat / the Updates feed).
 *
 * Two variants:
 *   - `capture` (crew task Recap tab): compose notes + photos, delete own.
 *   - `curate`  (supervisor closeout): read-only reference while the supervisor
 *     writes the blurb and picks the recap photos. No composer, no per-note edit
 *     — curation happens in the narrative editor + the gallery picker.
 */
import { useRef, useState } from 'react';
import { Eye, Loader2, Mic, Paperclip, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';
import type { RecapNote } from '@vari-crew/schemas';
import { useAddRecapNote, useDeleteRecapNote, useRecapNotes } from '@/api/recap-notes';
import { uploadProjectMedia } from '@/api/activity';
import { useDictation } from '@/lib/speech';
import { Button } from '@/components/ui/Button';
import { formatDate } from '@/lib/format';

type Staged = { id: string; filename: string; is_image: boolean; file_url: string | null };

export function RecapNotesCard({
  projectId,
  variant = 'capture',
  audience,
}: {
  projectId: string;
  /** `capture` (crew, composable) or `curate` (supervisor, read-only reference). */
  variant?: 'capture' | 'curate';
  /** Optional inline audience pill next to the header (tabbed task view). */
  audience?: string;
}) {
  const { data, isLoading } = useRecapNotes(projectId);
  const add = useAddRecapNote(projectId);
  const del = useDeleteRecapNote(projectId);
  const canCompose = variant === 'capture';

  const [body, setBody] = useState('');
  const [staged, setStaged] = useState<Staged[]>([]);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const { supported, listening, toggle } = useDictation((t) =>
    setBody((b) => (b ? `${b} ${t}` : t)),
  );

  const notes = data?.data ?? [];

  async function onPickFiles(files: FileList | null) {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      for (const file of Array.from(files)) {
        const att = await uploadProjectMedia(projectId, file);
        setStaged((s) => [
          ...s,
          {
            id: att.id,
            filename: att.original_filename,
            is_image: (att.mime_type ?? '').startsWith('image/'),
            file_url: att.file_url ?? null,
          },
        ]);
      }
    } catch (e) {
      toast.error(`Upload failed: ${(e as Error).message}`);
    } finally {
      setUploading(false);
    }
  }

  const canSave = !add.isPending && !uploading && (!!body.trim() || staged.length > 0);

  function submit() {
    if (!canSave) return;
    add.mutate(
      {
        body: body.trim() || '(photos)',
        source: 'text',
        attachment_ids: staged.map((s) => s.id),
      },
      {
        onSuccess: () => {
          setBody('');
          setStaged([]);
          toast.success('Recap note added');
        },
        onError: (e) => toast.error(`Could not add note: ${e.message}`),
      },
    );
  }

  return (
    <div className="space-y-4">
      {/* Composer — crew capture only. */}
      {canCompose && (
        <div className="bg-bg rounded-2xl border border-line p-4 space-y-2">
          <div className="flex items-center justify-between gap-2">
            <span className="text-[10px] uppercase tracking-widest text-ink-faint">Recap notes</span>
            {audience && (
              <span className="inline-flex items-center gap-1 rounded-full bg-accent/10 text-accent px-2 py-0.5 text-[10px] font-medium">
                <Eye className="w-3 h-3" /> {audience}
              </span>
            )}
          </div>
          <p className="text-[11px] text-ink-faint">
            Photos + a line of context for the closeout. Leo and the recap builder use these — the
            requester isn&rsquo;t notified.
          </p>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="What did you see? e.g. Second-floor install complete — showroom ready."
            rows={3}
            className="w-full rounded-lg border border-line bg-fill-soft p-2.5 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-accent"
          />

          {staged.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {staged.map((s, i) => (
                <div
                  key={s.id}
                  className="relative w-16 h-16 rounded-lg overflow-hidden border border-line bg-fill-soft"
                >
                  {s.is_image && s.file_url ? (
                    <img src={s.file_url} alt={s.filename} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full grid place-items-center text-[10px] text-ink-faint px-1 text-center">
                      {s.filename.slice(0, 12)}
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => setStaged((arr) => arr.filter((_, j) => j !== i))}
                    className="absolute top-0.5 right-0.5 bg-ink/70 text-bg rounded-full p-0.5"
                    aria-label="Remove photo"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          )}

          <input
            ref={inputRef}
            type="file"
            accept="image/*,video/*"
            capture="environment"
            multiple
            className="hidden"
            onChange={(e) => onPickFiles(e.target.files)}
          />

          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
            >
              {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Paperclip className="w-3.5 h-3.5" />}
              Photo / video
            </Button>
            {supported && (
              <Button
                type="button"
                size="sm"
                variant={listening ? 'default' : 'outline'}
                onClick={toggle}
              >
                <Mic className="w-3.5 h-3.5" /> {listening ? 'Listening…' : 'Dictate'}
              </Button>
            )}
            <Button size="sm" className="flex-1 min-w-[7rem]" onClick={submit} disabled={!canSave}>
              {add.isPending && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              Save note
            </Button>
          </div>
        </div>
      )}

      {/* Captured notes — the log Leo + AI read as source material. */}
      <div className="bg-bg rounded-2xl border border-line p-4 space-y-3">
        <div className="text-[10px] uppercase tracking-widest text-ink-faint">
          {canCompose ? 'Captured notes' : 'Crew notes · reference'}
        </div>
        {!canCompose && (
          <p className="text-[11px] text-ink-faint -mt-1">
            What the crew captured on site. These inform the summary + photo picks — they aren&rsquo;t
            shown to the requester.
          </p>
        )}
        {isLoading ? (
          <div className="py-3 grid place-items-center">
            <Loader2 className="w-4 h-4 animate-spin text-ink-faint" />
          </div>
        ) : notes.length === 0 ? (
          <p className="text-sm text-ink-faint">No recap notes yet.</p>
        ) : (
          <ul className="space-y-3">
            {notes.map((n) => (
              <RecapNoteRow
                key={n.id}
                note={n}
                canDelete={canCompose}
                onDelete={() =>
                  del.mutate(n.id, {
                    onError: (e) => toast.error(`Delete failed: ${e.message}`),
                  })
                }
              />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function RecapNoteRow({
  note,
  canDelete,
  onDelete,
}: {
  note: RecapNote;
  canDelete: boolean;
  onDelete: () => void;
}) {
  const photos = note.attachments.filter((a) => a.is_image);
  const hasText = !!note.body && note.body !== '(photos)';

  return (
    <li className="rounded-lg border border-line p-3 space-y-2">
      {photos.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {photos.map((a) =>
            a.file_url ? (
              <a key={a.id} href={a.file_url} target="_blank" rel="noreferrer">
                <img
                  src={a.file_url}
                  alt={a.filename}
                  className="w-20 h-20 object-cover rounded-md border border-line"
                />
              </a>
            ) : null,
          )}
        </div>
      )}

      {hasText && <p className="text-sm whitespace-pre-wrap">{note.body}</p>}

      <div className="flex items-center justify-between gap-2">
        <div className="text-[11px] text-ink-faint">
          {note.author_display_name}
          {` · ${formatDate(note.created_at)}`}
        </div>
        {canDelete && (
          <button
            onClick={onDelete}
            className="inline-flex items-center text-[11px] text-ink-faint hover:text-status-blocked-fg"
            title="Remove note"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </li>
  );
}
